// PageFlow: the editor. It owns the canvas, the selection, the history and the public API.
import { resolveElement, debounce, hFor, childBlocks, escapeHtml } from './dom.js';
import { createTranslator } from '../i18n/index.js';
import { createCanvas } from './canvas.js';
import { importHtml, createBlock, markBlock } from './parser.js';
import { exportHtml, cleanClone, formatHtml, emptyStyle } from './serializer.js';
import { History, pathOf, nodeAt } from './history.js';
import { blockOf, getBlock, canContain } from './registry.js';
import { breakpointForWidth, rebalanceRow } from './grid.js';
import { Overlay } from './overlay.js';
import { DragController } from './dnd.js';
import { TextEditing } from '../text/rte.js';
import { Media } from './media.js';
import { FileTransfer } from './files.js';
import { App } from '../ui/app.js';

export const VERSION = '2.0.0';

const DEFAULTS = {
    html: null,
    input: null,
    locale: 'en',
    i18n: {},
    canvasStyles: undefined,
    canvasCss: '',
    canvasScripts: [], // scripts loaded in the canvas (for a text editor adapter)
    baseUrl: '',
    rootClass: '',
    bodyClass: '',
    height: null,
    mode: 'full', // full | compact
    theme: 'light', // light | dark | auto
    blocks: null, // allow-list of block or preset ids, in palette order
    sections: true, // true, false or an array of section templates
    devices: true,
    device: null, // desktop, tablet or mobile at start (default: mobile on small screens, desktop otherwise)
    toolbar: { undo: true, devices: true, guides: true, preview: true, code: true, copy: true, fullscreen: true, layers: true },
    sanitize: true,
    textEditor: null,
    mediaLibrary: null,
    upload: null,
    uploadUrl: '',
    inlineImages: false, // true: without an upload address, pictures are kept in the page (base64)
    uploadField: 'upload',
    uploadData: {},
    csrfToken: '',
    maxUploadSize: 10 * 1024 * 1024,
    videoPrivacy: true,
    autofocus: false,
    historyLimit: 100,
    autosave: false, // true or a storage key: keeps a draft in the browser until the form is sent
    savedBlocks: true, // "Save as a reusable block" and the Saved group of the palette
    storage: null, // {load() => list, save(list)} to keep saved blocks elsewhere than in the browser
    onChange: null,
    onReady: null,
    actions: [], // extra buttons of the top bar: [{label, icon, onClick(editor), primary}]
    contentLang: '', // lang of the canvas document (default: the page's)
    rtl: false, // right-to-left content
    nonce: '', // CSP nonce of the styles and scripts PageFlow adds
    injectStyles: true, // false: load dist/pageflow.css yourself (strict CSP)
    desktopWidth: 1024, // smallest width of the desktop preview, scaled down to fit
    imageMaxSize: 1600, // longest side of pictures kept in the page (inlineImages)
    csrfField: 'csrf_token', // name of the token field sent with uploads
    uploadHeaders: {}, // extra headers of the upload request
};

/** PageFlow 1.x options and labels, still accepted (given: the options of the host, before the defaults). */
function legacyOptions(options, container, given) {
    const o = { ...options };
    if (o.exportToInput && !o.input) o.input = container.getAttribute('rel') || null;
    const labels = { copyCodeLabel: 'action.copyCode', editCodeLabel: 'action.code', codeCopiedMessage: 'notice.copied' };
    o.i18n = { ...(o.i18n || {}) };
    for (const [legacy, key] of Object.entries(labels)) if (o[legacy] && !o.i18n[key]) o.i18n[key] = o[legacy];
    o.toolbar = { ...DEFAULTS.toolbar, ...(o.toolbar || {}) };
    if (o.showExportBtn === false) o.toolbar.copy = false;
    if (o.showImportBtn === false) o.toolbar.code = false;
    if (o.removeUrl) console.info('PageFlow: removeUrl is no longer used; deleting a block never deletes a file on the server.');
    if (o.editorConfig) console.info('PageFlow: editorConfig (CKEditor) is no longer used; see the textEditor option.');
    if (container.classList.contains('pflow-compact') && !given.mode) o.mode = 'compact';
    return o;
}

export class PageFlow {
    constructor(target, options = {}) {
        const container = resolveElement(target);
        if (!container) throw new Error('PageFlow: target element not found.');
        if (container.pageflow) container.pageflow.destroy();
        this.container = container;
        const initialContent = container.innerHTML.trim();
        this.options = legacyOptions({ ...DEFAULTS, ...options }, container, options);
        this.t = createTranslator(this.options.locale, this.options.i18n);
        this.listeners = {};
        this.selected = null;
        this.device = 'desktop';
        this.preview = false;
        this.history = new History(this.options.historyLimit);
        this.input = resolveElement(this.options.input);
        this.clipboard = null;
        this.destroyed = false;
        container.pageflow = this;

        if (typeof this.options.onChange === 'function') this.on('change', this.options.onChange);

        const initial = this.options.html !== null && this.options.html !== undefined
            ? String(this.options.html)
            : this.input ? this.input.value : initialContent;
        container.replaceChildren();

        // A change being typed is recorded after a pause; meanwhile Undo must already be available.
        const commitLater = debounce(() => this.commit(), 600);
        this.changed = Object.assign(() => {
            commitLater();
            if (this.app) this.app.historyChanged();
        }, { flush: () => commitLater.flush(), cancel: () => commitLater.cancel(), pending: () => commitLater.pending() });
        this.app = new App(this);
        this.ready = createCanvas(this.app.stage, this.options, this.t).then(canvas => {
            if (this.destroyed) return this;
            this.canvas = canvas;
            this.ctx = {
                doc: canvas.doc,
                h: hFor(canvas.doc),
                t: this.t,
                options: this.options,
                editor: this,
                retag: (el, tag, reselect = true) => this.retag(el, tag, reselect),
            };
            this.overlay = new Overlay(this);
            this.dnd = new DragController(this);
            this.text = new TextEditing(this);
            this.media = new Media(this);
            this.files = new FileTransfer(this);
            this.bindCanvas();
            this.bindForm();
            canvas.root.append(importHtml(initial, this.ctx));
            this.history.reset(this.snapshot());
            this.initAutosave();
            this.app.ready();
            this.overlay.refresh();
            if (canvas.gridFallback) {
                console.warn('PageFlow: no Bootstrap grid in the canvas; check the canvasStyles option.', this.options.canvasStyles);
                this.notify(this.t('notice.noGrid'), 'warning', 9000);
            }
            this.emit('ready', this);
            if (typeof this.options.onReady === 'function') this.options.onReady(this);
            return this;
        });
    }

    // ---- Events --------------------------------------------------------------------------------------

    on(name, fn) {
        (this.listeners[name] = this.listeners[name] || []).push(fn);
        return this;
    }

    off(name, fn) {
        this.listeners[name] = (this.listeners[name] || []).filter(listener => listener !== fn);
        return this;
    }

    emit(name, payload) {
        (this.listeners[name] || []).forEach(fn => {
            try {
                fn.call(this, payload, this);
            } catch (error) {
                console.error(error);
            }
        });
        this.container.dispatchEvent(new CustomEvent(`pageflow:${name}`, { detail: payload, bubbles: true }));
    }

    notify(message, type = 'info', duration = 3000, action = null) {
        if (this.app) this.app.toast(message, type, duration, action);
    }

    // ---- Canvas events ---------------------------------------------------------------------------------

    bindCanvas() {
        const { doc, win, root } = this.canvas;
        const blockAt = target => {
            const el = target && target.closest ? target.closest('[data-pf]') : null;
            return el && root.contains(el) ? el : null;
        };
        doc.addEventListener('pointermove', event => {
            if (!this.dnd.active) this.overlay.setHover(blockAt(event.target));
        });
        doc.addEventListener('pointerleave', () => this.overlay.setHover(null));
        doc.addEventListener('click', event => {
            if (this.preview) {
                if (event.target.closest('a')) event.preventDefault();
                return;
            }
            if (event.target.closest('.pf-ui')) return;
            // Links, summaries and forms of the content never act while editing.
            if (event.target.closest('a, summary, button, label')) event.preventDefault();
            const block = blockAt(event.target);
            if (!block) {
                if (!this.text.active || !this.text.active.contains(event.target)) this.select(null);
                return;
            }
            const region = event.target.closest('[data-pf-edit]');
            const editable = region && blockAt(region) === block ? region : null;
            if (this.text.active && editable === this.text.active) return;
            this.select(block);
            if (editable) this.text.activate(editable, { x: event.clientX, y: event.clientY });
        });
        doc.addEventListener('dblclick', event => {
            if (this.preview) return;
            const block = blockAt(event.target);
            const def = blockOf(block);
            if (def && (def.name === 'image' || (def.name === 'card' && event.target.closest('img')))) {
                event.preventDefault();
                this.app.inspector.pickMedia(block);
            }
        });
        doc.addEventListener('submit', event => event.preventDefault(), true);
        // The browser's own drag and drop would copy pictures and links of the page into a text (with
        // absolute addresses) or insert foreign markup: in the canvas, blocks move with their handle.
        doc.addEventListener('dragstart', event => {
            if (!this.preview) event.preventDefault();
        });
        doc.addEventListener('drop', event => {
            const files = event.dataTransfer && Array.from(event.dataTransfer.types || []).includes('Files');
            if (!files) event.preventDefault();
        });
        doc.addEventListener('keydown', event => this.keydown(event));
        // Blocks go through the real clipboard: they can be pasted in another page or another tab.
        this.onCopy = event => this.copyBlock(event, false);
        this.onCut = event => this.copyBlock(event, true);
        this.onPaste = event => this.pasteFromClipboard(event);
        for (const target of [doc, this.container]) {
            target.addEventListener('copy', this.onCopy);
            target.addEventListener('cut', this.onCut);
            target.addEventListener('paste', this.onPaste);
        }
        win.addEventListener('scroll', () => this.overlay.schedule(), { passive: true });
        win.addEventListener('resize', () => this.overlay.schedule());
        this.resizeObserver = new ResizeObserver(() => this.overlay.schedule());
        this.resizeObserver.observe(root);
        // Images change the layout once loaded.
        root.addEventListener('load', () => this.overlay.schedule(), true);
        this.onHostKeydown = event => {
            if (this.container.contains(event.target)) this.keydown(event);
        };
        document.addEventListener('keydown', this.onHostKeydown);
    }

    bindForm() {
        const form = this.input ? this.input.closest('form') : this.container.closest('form');
        if (!form) return;
        this.form = form;
        this.onSubmit = () => {
            this.flush();
            this.clearDraft();
        };
        form.addEventListener('submit', this.onSubmit, true);
    }

    keydown(event) {
        if (this.dnd && this.dnd.active) return;
        const target = event.target;
        const typing = target && (target.isContentEditable || /^(input|textarea|select)$/i.test(target.localName));
        const mod = event.ctrlKey || event.metaKey;
        const key = event.key.toLowerCase();
        if (mod && key === 'z' && !typing) {
            event.preventDefault();
            if (event.shiftKey) this.redo();
            else this.undo();
            return;
        }
        if (mod && key === 'y' && !typing) {
            event.preventDefault();
            this.redo();
            return;
        }
        if (typing || !this.selected) return;
        // A button of the interface keeps its own keys (Delete on a palette entry must not remove a block).
        const onControl = target && target.ownerDocument === document && target.closest && target.closest('button, a, [role="button"], summary');
        if (onControl && key !== 'escape') return;
        if (key === 'delete' || key === 'backspace') {
            event.preventDefault();
            this.removeSelected();
        } else if (mod && key === 'd') {
            event.preventDefault();
            this.duplicateSelected();
        } else if (event.altKey && (key === 'arrowup' || key === 'arrowdown')) {
            event.preventDefault();
            this.moveSelected(key === 'arrowup' ? -1 : 1);
        } else if (key === 'escape') {
            event.preventDefault();
            if (this.parentBlock(this.selected)) this.selectParent();
            else this.select(null);
        } else if (key === 'enter') {
            const region = this.selected.matches('[data-pf-edit]') ? this.selected : this.selected.querySelector('[data-pf-edit]');
            if (region && blockOf(this.selected)) {
                event.preventDefault();
                this.text.activate(region);
            }
        }
    }

    // ---- Selection -------------------------------------------------------------------------------------

    select(el) {
        if (el && !this.canvas.root.contains(el)) el = null;
        if (this.text && this.text.active && (!el || !el.contains(this.text.active))) this.text.deactivate();
        if (this.selected === el) return;
        this.selected = el;
        this.overlay.schedule();
        this.app.selectionChanged();
        this.emit('select', el);
    }

    parentBlock(el) {
        if (!el) return null;
        const root = this.canvas.root;
        let parent = el.parentElement;
        while (parent && parent !== root && !parent.hasAttribute('data-pf')) parent = parent.parentElement;
        return parent && parent !== root ? parent : null;
    }

    selectParent() {
        const parent = this.parentBlock(this.selected);
        if (parent) this.select(parent);
    }

    // ---- Editing ---------------------------------------------------------------------------------------

    /** Inserts a palette item (block, preset or section) at a drop spot, or near the selection. */
    insertItem(item, spot = null) {
        const name = item.block;
        const fragment = createBlock(name, this.ctx, item.html || null);
        if (!fragment) return null;
        const first = fragment.firstElementChild;
        const target = spot || this.insertionSpot(blockOf(first) ? blockOf(first).name : name);
        if (!target) {
            this.notify(this.t('notice.cannotInsert'), 'warning');
            return null;
        }
        target.parent.insertBefore(fragment, target.before);
        if (blockOf(first) && blockOf(first).kind === 'col' && blockOf(target.parent).kind === 'row') rebalanceRow(target.parent);
        this.select(first);
        this.reveal(first);
        this.commit();
        this.emit('insert', first);
        return first;
    }

    /** Where a click on a palette item inserts it: after the selection, inside it, or at the end. */
    insertionSpot(name) {
        const root = this.canvas.root;
        let el = this.selected;
        while (el) {
            const parent = this.parentBlock(el) || root;
            const def = blockOf(el);
            if ((def.kind === 'flow' || def.kind === 'col' || def.kind === 'row') && canContain(el, name)) return { parent: el, before: null };
            if (parent === root ? canContain(null, name, true) : canContain(parent, name)) return { parent, before: el.nextElementSibling };
            el = this.parentBlock(el);
        }
        return canContain(null, name, true) ? { parent: root, before: null } : null;
    }

    moveBlock(el, spot) {
        if (!spot || spot.before === el || (spot.parent === el.parentElement && spot.before === el.nextElementSibling)) return;
        if (el.contains(spot.parent)) return;
        const from = el.parentElement;
        spot.parent.insertBefore(el, spot.before);
        if (blockOf(el).kind === 'col' && from !== spot.parent) {
            if (!childBlocks(from).length && blockOf(from)) from.remove();
            else rebalanceRow(from);
            rebalanceRow(spot.parent);
        }
        this.select(el);
        this.commit();
    }

    moveSelected(direction) {
        const el = this.selected;
        if (!el) return;
        let sibling = direction < 0 ? el.previousElementSibling : el.nextElementSibling;
        while (sibling && !sibling.hasAttribute('data-pf')) sibling = direction < 0 ? sibling.previousElementSibling : sibling.nextElementSibling;
        if (!sibling) return;
        if (direction < 0) sibling.before(el);
        else sibling.after(el);
        this.reveal(el);
        this.overlay.schedule();
        this.commit();
    }

    duplicateSelected() {
        const el = this.selected;
        if (!el) return;
        this.text.deactivate();
        const copy = el.cloneNode(true);
        copy.querySelectorAll('[contenteditable]').forEach(node => node.removeAttribute('contenteditable'));
        // An id must stay unique in a page.
        copy.removeAttribute('id');
        copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        el.after(copy);
        if (blockOf(el).kind === 'col') rebalanceRow(el.parentElement);
        this.select(copy);
        this.commit();
    }

    removeSelected() {
        const el = this.selected;
        if (!el) return;
        const parent = this.parentBlock(el);
        const next = el.nextElementSibling && el.nextElementSibling.hasAttribute('data-pf') ? el.nextElementSibling
            : el.previousElementSibling && el.previousElementSibling.hasAttribute('data-pf') ? el.previousElementSibling : parent;
        this.text.deactivate();
        el.remove();
        if (parent && blockOf(parent).kind === 'row') {
            if (!childBlocks(parent).length) parent.remove();
            else rebalanceRow(parent);
        }
        this.selected = null;
        this.select(next && next.isConnected ? next : null);
        this.commit();
        this.emit('remove', el);
    }

    /** Replaces an element by another tag, keeping its attributes and content. */
    retag(el, tag, reselect = true) {
        if (!el || el.localName === tag) return el;
        const replacement = this.canvas.doc.createElement(tag);
        for (const attr of Array.from(el.attributes)) replacement.setAttribute(attr.name, attr.value);
        replacement.append(...el.childNodes);
        el.replaceWith(replacement);
        if (reselect && this.selected === el) {
            this.selected = replacement;
            this.overlay.schedule();
        }
        return replacement;
    }

    /**
     * Scrolls the canvas, and only the canvas, to show a block (scrollIntoView would also scroll the
     * page around the editor, in a CMS for instance).
     */
    reveal(el, center = false) {
        if (!el || !el.isConnected) return;
        const win = this.canvas.win;
        const rect = el.getBoundingClientRect();
        const margin = 64 * this.uiScale();
        const height = win.innerHeight;
        let delta = 0;
        if (center) delta = rect.top + rect.height / 2 - height / 2;
        else if (rect.top < margin) delta = rect.top - margin;
        else if (rect.bottom > height - 16) delta = Math.min(rect.top - margin, rect.bottom - height + 16);
        if (delta) win.scrollBy({ top: delta, behavior: 'smooth' });
    }

    /** Clean HTML of one block (for copy and paste). */
    blockHtml(el) {
        const holder = this.canvas.doc.createElement('div');
        holder.append(el.cloneNode(true));
        return exportHtml(holder, this.ctx);
    }

    /** Copy or cut of the selected block, when no text is selected or edited. */
    copyBlock(event, cut) {
        if (!this.selected || this.preview || this.text.active || isTyping(event.target)) return;
        const selection = (event.target.ownerDocument || document).getSelection();
        if (selection && !selection.isCollapsed) return;
        const html = this.blockHtml(this.selected);
        this.clipboard = html;
        if (event.clipboardData) {
            event.clipboardData.setData('text/html', html);
            event.clipboardData.setData('text/plain', html);
        }
        event.preventDefault();
        if (cut) this.removeSelected();
    }

    /** Paste outside a text: HTML becomes blocks, plain text becomes paragraphs. */
    pasteFromClipboard(event) {
        if (this.preview || this.text.active || isTyping(event.target) || !this.canvas) return;
        if (this.files.paste(event)) return;
        const data = event.clipboardData;
        const html = data ? data.getData('text/html') : '';
        const text = data ? data.getData('text/plain') : '';
        let source = html || (/^\s*</.test(text) ? text : '');
        if (!source && text.trim()) source = text.trim().split(/\r?\n\s*\r?\n/).map(part => `<p>${escapeHtml(part).replace(/\r?\n/g, '<br>')}</p>`).join('');
        if (!source && this.clipboard) source = this.clipboard;
        if (!source) return;
        event.preventDefault();
        // Office and browsers wrap copied HTML in a whole document: keep its body.
        const body = source.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        this.pasteHtml(body ? body[1].replace(/<!--(?:Start|End)Fragment-->/g, '') : source);
    }

    pasteHtml(html) {
        const fragment = importHtml(html, this.ctx);
        const first = fragment.firstElementChild;
        if (!first) return;
        const name = blockOf(first) ? blockOf(first).name : 'html';
        const spot = this.insertionSpot(name);
        if (!spot) return;
        spot.parent.insertBefore(fragment, spot.before);
        this.select(first);
        this.commit();
    }

    // ---- History and output ----------------------------------------------------------------------------

    snapshot() {
        const clone = this.canvas.root.cloneNode(true);
        clone.querySelectorAll('[contenteditable], [spellcheck]').forEach(node => {
            node.removeAttribute('contenteditable');
            node.removeAttribute('spellcheck');
        });
        // An empty style left by the browser changes nothing on the page: it is no step of the history.
        clone.querySelectorAll('[style]').forEach(node => {
            if (emptyStyle(node.getAttributeNode('style'))) node.removeAttribute('style');
        });
        return { html: clone.innerHTML, selection: this.selected ? pathOf(this.selected, this.canvas.root) : null };
    }

    /** Records the current state as one step of the history, then updates the output. */
    commit() {
        if (!this.canvas) return;
        this.changed.cancel();
        if (this.history.push(this.snapshot())) {
            this.sync();
            this.emit('change', this);
        }
        this.app.historyChanged();
        this.overlay.schedule();
    }

    restore(state) {
        if (!state) return;
        this.text.deactivate();
        this.canvas.root.innerHTML = state.html;
        this.selected = null;
        this.select(nodeAt(state.selection, this.canvas.root));
        this.sync();
        this.app.historyChanged();
        this.app.contentChanged();
        this.emit('change', this);
    }

    undo() {
        this.flush();
        this.restore(this.history.undo());
    }

    redo() {
        // A change not recorded yet comes first: it ends the future of the history.
        this.flush();
        this.restore(this.history.redo());
    }

    /** A change typed (or made) but not recorded in the history yet. */
    hasPendingChanges() {
        if (this.changed.pending()) return true;
        const text = this.text;
        return !!(text && text.active && text.active.innerHTML !== text.before);
    }

    /**
     * Ends the text being typed and writes the output now (before a form is sent). With keepEditing, the output is
     * written and the typing goes on (a periodic backup of the host); a plugged text editor still ends, as it may
     * hold its text outside the page.
     */
    flush({ keepEditing = false } = {}) {
        if (!this.canvas) return;
        if (this.text.active && !(keepEditing && !this.text.adapter)) this.text.deactivate();
        this.changed.flush();
        this.commit();
        this.sync();
    }

    // ---- Draft kept in the browser (autosave option) ---------------------------------------------------

    draftKey() {
        const o = this.options.autosave;
        if (!o) return null;
        if (typeof o === 'string') return `pageflow:${o}`;
        const name = (this.input && (this.input.name || this.input.id)) || this.container.id || 'editor';
        return `pageflow:${location.pathname}:${name}`;
    }

    initAutosave() {
        const key = this.draftKey();
        if (!key) return;
        this.baseHash = hashString(this.getHTML());
        this.saveDraft = debounce(() => {
            try {
                const html = this.getHTML();
                if (hashString(html) === this.baseHash) localStorage.removeItem(key);
                else localStorage.setItem(key, JSON.stringify({ html, base: this.baseHash, time: Date.now() }));
            } catch (error) {
                // private browsing or full storage: the editor works without drafts
            }
        }, 800);
        this.on('change', () => this.saveDraft());
        let draft = null;
        try {
            draft = JSON.parse(localStorage.getItem(key) || 'null');
        } catch (error) {
            draft = null;
        }
        if (!draft || !draft.html) return;
        // A draft made from another version of the page (saved since, elsewhere) is obsolete.
        if (draft.base !== this.baseHash) {
            this.clearDraft();
            return;
        }
        const when = new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(draft.time));
        this.notify(this.t('notice.draftFound', { date: when }), 'info', 20000, {
            label: this.t('action.restore'),
            onClick: () => this.setHTML(draft.html),
        });
    }

    clearDraft() {
        const key = this.draftKey();
        if (!key) return;
        if (this.saveDraft) this.saveDraft.cancel();
        try {
            localStorage.removeItem(key);
        } catch (error) {
            // nothing to clear
        }
    }

    // ---- Saved blocks ------------------------------------------------------------------------------------

    storage() {
        if (this.options.storage) return this.options.storage;
        const key = 'pageflow:saved-blocks';
        return {
            load: () => {
                try {
                    return JSON.parse(localStorage.getItem(key) || '[]');
                } catch (error) {
                    return [];
                }
            },
            save: list => {
                try {
                    localStorage.setItem(key, JSON.stringify(list));
                } catch (error) {
                    this.notify(this.t('error.storage'), 'error');
                }
            },
        };
    }

    async savedBlocks() {
        if (this.options.savedBlocks === false) return [];
        const list = await this.storage().load();
        return Array.isArray(list) ? list.filter(item => item && item.html && item.label) : [];
    }

    async saveBlock(el, label) {
        const def = blockOf(el);
        if (!def || !label) return;
        const list = await this.savedBlocks();
        list.unshift({ id: `saved-${Date.now().toString(36)}`, label: String(label).slice(0, 60), block: def.name, html: this.blockHtml(el) });
        await this.storage().save(list.slice(0, 100));
        this.app.savedChanged();
        this.notify(this.t('saved.done'), 'success');
    }

    async removeSavedBlock(id) {
        const list = (await this.savedBlocks()).filter(item => item.id !== id);
        await this.storage().save(list);
        this.app.savedChanged();
    }

    sync() {
        if (!this.canvas) return;
        this.app.contentChanged();
        if (!this.input) return;
        const html = this.getHTML();
        if (this.input.value === html) return;
        this.input.value = html;
        if (this.input.localName === 'textarea') this.input.textContent = html;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // ---- Public API ------------------------------------------------------------------------------------

    getHTML(options = {}) {
        if (!this.canvas) return '';
        return exportHtml(this.canvas.root, this.ctx, options);
    }

    setHTML(html, { history = true } = {}) {
        if (!this.canvas) {
            this.ready.then(() => this.setHTML(html, { history }));
            return this;
        }
        this.text.deactivate();
        this.selected = null;
        this.canvas.root.replaceChildren(importHtml(html, this.ctx));
        this.select(null);
        if (history) this.commit();
        else {
            this.history.reset(this.snapshot());
            this.sync();
            this.app.historyChanged();
        }
        return this;
    }

    /** Clean HTML of the page as a document fragment (for hosts that post-process the markup). */
    getFragment() {
        return cleanClone(this.canvas.root, this.ctx);
    }

    insertBlock(name, presetHtml = null) {
        return this.insertItem({ block: name, html: presetHtml });
    }

    breakpoint() {
        return breakpointForWidth(this.canvas ? this.canvas.iframe.clientWidth : 1200);
    }

    setDevice(device) {
        this.device = device;
        this.app.deviceChanged();
        this.emit('device', device);
        return this;
    }

    /** Size factor of the tools drawn in the canvas, so that they keep their size when it is scaled down. */
    uiScale() {
        const scale = this.app && this.app.scale ? this.app.scale : 1;
        return Math.min(1.8, 1 / scale);
    }

    isPreview() {
        return this.preview;
    }

    setPreview(on) {
        this.preview = !!on;
        if (this.preview) this.select(null);
        this.canvas.doc.body.classList.toggle('pf-preview', this.preview);
        this.app.previewChanged();
        this.overlay.schedule();
        return this;
    }

    destroy() {
        if (this.destroyed) return;
        this.destroyed = true;
        try {
            this.flush();
        } catch (error) {
            // the canvas may never have loaded
        }
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit, true);
        document.removeEventListener('keydown', this.onHostKeydown);
        if (this.onCopy) {
            this.container.removeEventListener('copy', this.onCopy);
            this.container.removeEventListener('cut', this.onCut);
            this.container.removeEventListener('paste', this.onPaste);
        }
        this.app.destroy();
        this.container.replaceChildren();
        delete this.container.pageflow;
        this.emit('destroy', this);
        this.listeners = {};
    }
}

function isTyping(target) {
    return !!target && (target.isContentEditable || /^(input|textarea|select)$/i.test(target.localName || ''));
}

/** A short hash to recognize a version of the page (djb2). */
function hashString(text) {
    let hash = 5381;
    for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
    return (hash >>> 0).toString(36);
}

export { formatHtml, markBlock, getBlock };
